// ink-component
import * as React from 'react';
import { ThemeProvider } from 'styled-components';

import { FeedbackVariants } from '../../../_common/Types/GlobalTypes';
import CoreRow from '../../_common/CoreRow';
import colors from '../../../../assets/styled/colors';

import { TwiddleRowContext } from '../NewTableTwiddle/contexts';
import { NewTableVirtualContext } from '../NewTableVirtualWrapper/NewTableVirtualWrapper';

export interface NewTableRowProps extends React.TableHTMLAttributes<HTMLTableRowElement> {
  /** Marks the row as selected. */
  selected?: boolean;
  /** Apply indentation to the row */
  indent?: number;
  /** Apply special style properties for certain child types inside the cell */
  preset?: 'totals' | 'lowContrast';
  /** Semantic variants, based on intent */
  variant?: Extract<
    FeedbackVariants,
    'feedback-positive' | 'feedback-notice' | 'feedback-negative' | 'feedback-neutral' | 'feedback-informational'
  >;
  /** Use only when `variant` and `preset` prove insufficient. Sets the background color of all cells in a row. See Usage for a list of available colors. (This color is overwritten by bgColor of [NewTableCell](/components/NewTable.Cell).) */
  bgColor?: keyof typeof colors;

  /** @ignore */
  transform?: React.CSSProperties['transform'];
}

/**
 * @group NewTable
 * @purpose Wraps around a horizontal set of cells
 * @categories table
 * @HTMLType HTMLTableRowElement
 */
const NewTableRow = React.forwardRef<HTMLTableRowElement, NewTableRowProps>((props, forwardedRef) => {
  const { level } = React.useContext(TwiddleRowContext);
  const { style, className, transform, children, variant, bgColor, selected, indent, preset, ...otherProps } = props;

  // eslint-disable-next-line ssr-friendly/no-dom-globals-in-react-fc
  const ref = React.useRef<HTMLTableRowElement>(null);

  React.useImperativeHandle(forwardedRef, () => ref.current as HTMLTableRowElement);

  const handleKeyPress = e => {
    if (!otherProps.onClick) return;
    if ((e.key === ' ' || e.key === 'Enter') && e.target === ref.current) {
      e.preventDefault();
      otherProps.onClick(e);
    }
  };

  const clickableProps = otherProps.onClick && {
    tabIndex: 0,
    // note on the role of button here: technically, <tr>s should not have
    // roles as screen readers won't read them properly. we should look into
    // a better way of truly making clickable table rows accessible.
    role: 'button',
    onKeyPress: handleKeyPress,
  };

  const virtual = React.useContext(NewTableVirtualContext);

  if (virtual?.section === 'body' && !(transform || otherProps['data-index'])) {
    throw new Error(
      'NewTable.Row: Virtualization requires spreading ...virtualizer.getRowProps(virtualRow) to NewTable.Row.',
    );
  }

  return (
    <ThemeProvider
      theme={tableTheme => ({
        ...tableTheme,
        rowVariant: variant,
        rowBgColor: bgColor,
        rowIndent: indent,
        rowPreset: preset,
        rowHasClick: !!otherProps.onClick,
        rowSelected: selected,
        twiddleLevel: level,
      })}
    >
      <CoreRow {...clickableProps} {...otherProps} virtual={virtual} style={{ transform }} data-level={level} ref={ref}>
        {children}
      </CoreRow>
    </ThemeProvider>
  );
});

NewTableRow.defaultProps = {
  selected: false,
  bgColor: 'white',
  indent: undefined,
  preset: undefined,
};

NewTableRow.displayName = 'NewTable.Row';

export default NewTableRow;
